import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useCurrency } from '../contexts/CurrencyContext';
import { useWishlist } from '../contexts/WishlistContext';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Heart, Trash2, ShoppingCart, ChevronRight, Sparkles } from 'lucide-react';
import Header from './Header';
import Footer from './Footer';

const WishlistPage = () => {
  const { isAuthenticated } = useAuth();
  const { currentLanguage } = useLanguage();
  const { wishlist, loading, removeFromWishlist } = useWishlist();
  const navigate = useNavigate();

  const formatPrice = useCurrency().formatPrice;

  const getProductName = (product) => {
    if (!product) return '';
    return product.name[currentLanguage] || product.name.en || product.name;
  };

  const hasVariants = (product) => product?.variants && product.variants.length > 1;

  const getDisplayPrice = (product) => {
    if (!product) return 0;
    if (product.variants && product.variants.length > 0) {
      const prices = product.variants.map(v => v.price);
      return Math.min(...prices);
    }
    return product.price;
  };

  const translations = {
    en: {
      title: 'My Wishlist',
      subtitle: 'Products you love, saved for later',
      empty: 'Your wishlist is empty',
      emptyDesc: 'Save products you love to your wishlist',
      browseProducts: 'Browse Products',
      login: 'Please login to view your wishlist',
      loginBtn: 'Login',
      remove: 'Remove',
      from: 'From',
      viewProduct: 'View Product'
    },
    es: {
      title: 'Mi Lista de Deseos',
      subtitle: 'Productos que te gustan, guardados para más tarde',
      empty: 'Tu lista de deseos está vacía',
      emptyDesc: 'Guarda los productos que te gustan en tu lista de deseos',
      browseProducts: 'Ver Productos',
      login: 'Inicia sesión para ver tu lista de deseos',
      loginBtn: 'Iniciar Sesión',
      remove: 'Eliminar',
      from: 'Desde',
      viewProduct: 'Ver Producto'
    },
    ru: {
      title: 'Избранное',
      subtitle: 'Товары, которые вам нравятся',
      empty: 'Ваш список избранного пуст',
      emptyDesc: 'Сохраняйте понравившиеся товары в избранное',
      browseProducts: 'Смотреть Продукты',
      login: 'Войдите, чтобы увидеть избранное',
      loginBtn: 'Войти',
      remove: 'Удалить',
      from: 'От',
      viewProduct: 'Смотреть'
    },
    de: {
      title: 'Meine Wunschliste',
      subtitle: 'Produkte, die Sie lieben, für später gespeichert',
      empty: 'Ihre Wunschliste ist leer',
      emptyDesc: 'Speichern Sie Produkte, die Sie interessieren, auf Ihrer Wunschliste',
      browseProducts: 'Produkte ansehen',
      login: 'Bitte melden Sie sich an, um Ihre Wunschliste zu sehen',
      loginBtn: 'Anmelden',
      remove: 'Entfernen',
      from: 'Ab',
      viewProduct: 'Produkt ansehen'
    },
    fr: {
      title: 'Ma liste de souhaits',
      subtitle: 'Les produits que vous aimez, enregistrés pour plus tard',
      empty: 'Votre liste de souhaits est vide',
      emptyDesc: 'Enregistrez les produits qui vous intéressent dans votre liste de souhaits',
      browseProducts: 'Parcourir les produits',
      login: 'Veuillez vous connecter pour voir votre liste de souhaits',
      loginBtn: 'Connexion',
      remove: 'Retirer',
      from: 'À partir de',
      viewProduct: 'Voir le produit'
    },
    it: {
      title: 'La mia lista dei desideri',
      subtitle: 'I prodotti che ami, salvati per dopo',
      empty: 'La tua lista dei desideri è vuota',
      emptyDesc: 'Salva i prodotti che ami nella tua lista dei desideri',
      browseProducts: 'Sfoglia i prodotti',
      login: 'Accedi per visualizzare la tua lista dei desideri',
      loginBtn: 'Accedi',
      remove: 'Rimuovi',
      from: 'Da',
      viewProduct: 'Vedi prodotto'
    },
    pl: {
      title: 'Moja lista życzeń',
      subtitle: 'Produkty, które kochasz, zapisane na później',
      empty: 'Twoja lista życzeń jest pusta',
      emptyDesc: 'Zapisz interesujące Cię produkty na liście życzeń',
      browseProducts: 'Przeglądaj produkty',
      login: 'Zaloguj się, aby zobaczyć swoją listę życzeń',
      loginBtn: 'Zaloguj się',
      remove: 'Usuń',
      from: 'Od',
      viewProduct: 'Zobacz produkt'
    },
    nl: {
      title: 'Mijn verlanglijst',
      subtitle: 'Producten die u leuk vindt, opgeslagen voor later',
      empty: 'Uw verlanglijst is leeg',
      emptyDesc: 'Bewaar producten die u interesseren op uw verlanglijst',
      browseProducts: 'Producten bekijken',
      login: 'Log in om uw verlanglijst te bekijken',
      loginBtn: 'Inloggen',
      remove: 'Verwijderen',
      from: 'Vanaf',
      viewProduct: 'Product bekijken'
    },
    pt: {
      title: 'A minha lista de desejos',
      subtitle: 'Produtos que adora, guardados para mais tarde',
      empty: 'A sua lista de desejos está vazia',
      emptyDesc: 'Guarde os produtos que lhe interessam na sua lista de desejos',
      browseProducts: 'Ver produtos',
      login: 'Inicie sessão para ver a sua lista de desejos',
      loginBtn: 'Iniciar sessão',
      remove: 'Remover',
      from: 'A partir de',
      viewProduct: 'Ver produto'
    }
  };

  const t = translations[currentLanguage] || translations.en;

  // Not authenticated
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-capsule-bg">
        <Header />
        
        {/* Hero Banner */}
        <div className="relative overflow-hidden bg-gradient-to-br from-gray-900 via-gray-800 to-capsule-teal-dark">
          <div className="absolute right-0 top-0 bottom-0 w-96 opacity-20">
            <svg viewBox="0 0 200 400" fill="none" className="w-full h-full">
              <defs>
                <linearGradient id="wishlistGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#14B8A6" />
                  <stop offset="100%" stopColor="#22D3EE" />
                </linearGradient>
              </defs>
              <path d="M50 0 Q120 50 50 100 Q-20 150 50 200 Q120 250 50 300 Q-20 350 50 400" 
                    stroke="url(#wishlistGradient)" strokeWidth="3" fill="none"/>
              <path d="M150 0 Q80 50 150 100 Q220 150 150 200 Q80 250 150 300 Q220 350 150 400" 
                    stroke="url(#wishlistGradient)" strokeWidth="3" fill="none"/>
            </svg>
          </div>
          
          <div className="relative max-w-7xl mx-auto px-4 py-16 md:py-20">
            <div className="max-w-3xl">
              <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-md px-4 py-2 rounded-full mb-4 border border-white/20">
                <Heart className="w-4 h-4 text-capsule-salmon" />
                <span className="text-sm font-medium text-capsule-salmon">Favorites</span>
              </div>
              
              <h1 className="text-3xl md:text-4xl font-bold text-white mb-3">{t.title}</h1>
              <p className="text-lg text-gray-300">{t.subtitle}</p>
            </div>
          </div>
        </div>
        
        <main className="container mx-auto px-4 py-16">
          <Card className="max-w-md mx-auto shadow-lg border-0 rounded-2xl">
            <CardContent className="text-center py-12">
              <div className="w-20 h-20 bg-capsule-salmon/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Heart className="w-10 h-10 text-capsule-salmon" />
              </div>
              <h2 className="text-xl font-bold text-gray-900 mb-2">{t.title}</h2>
              <p className="text-gray-600 mb-6">{t.login}</p>
              <Button
                onClick={() => navigate('/login')}
                className="bg-gradient-to-r from-capsule-teal to-capsule-aqua hover:from-capsule-teal-dark hover:to-capsule-teal text-white rounded-xl"
                data-testid="login-btn"
              >
                {t.loginBtn}
              </Button>
            </CardContent>
          </Card>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-capsule-bg">
      <Header />
      
      {/* Hero Banner - Capsule 8 Design */}
      <div className="relative overflow-hidden bg-gradient-to-br from-gray-900 via-gray-800 to-capsule-teal-dark">
        <div className="absolute right-0 top-0 bottom-0 w-96 opacity-20">
          <svg viewBox="0 0 200 400" fill="none" className="w-full h-full">
            <defs>
              <linearGradient id="wishlistGradient2" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#14B8A6" />
                <stop offset="100%" stopColor="#22D3EE" />
              </linearGradient>
            </defs>
            <path d="M50 0 Q120 50 50 100 Q-20 150 50 200 Q120 250 50 300 Q-20 350 50 400" 
                  stroke="url(#wishlistGradient2)" strokeWidth="3" fill="none"/>
            <path d="M150 0 Q80 50 150 100 Q220 150 150 200 Q80 250 150 300 Q220 350 150 400" 
                  stroke="url(#wishlistGradient2)" strokeWidth="3" fill="none"/>
          </svg>
        </div>
        
        <div className="absolute top-20 right-32 w-20 h-20 rounded-full bg-gradient-to-br from-capsule-rose-gold/30 to-capsule-salmon/20 blur-xl"></div>
        <div className="absolute bottom-10 right-48 w-16 h-16 rounded-full bg-gradient-to-br from-capsule-teal/30 to-capsule-aqua/20 blur-xl"></div>
        
        <div className="relative max-w-7xl mx-auto px-4 py-12 md:py-16">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-md px-4 py-2 rounded-full mb-4 border border-white/20">
              <Heart className="w-4 h-4 text-capsule-salmon" />
              <span className="text-sm font-medium text-capsule-salmon">Favorites</span>
            </div>
            
            <h1 className="text-3xl md:text-4xl font-bold text-white mb-3">{t.title}</h1>
            <p className="text-lg text-gray-300">{t.subtitle}</p>
          </div>
        </div>
      </div>

      <main className="container mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 -mt-8">
        <div className="max-w-6xl mx-auto">
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-capsule-teal"></div>
            </div>
          ) : wishlist.length === 0 ? (
            // Empty State
            <Card className="shadow-lg border-0 rounded-2xl">
              <CardContent className="text-center py-12 sm:py-16">
                <div className="w-20 h-20 bg-capsule-salmon/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Heart className="w-10 h-10 text-capsule-salmon" />
                </div>
                <h2 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2">{t.empty}</h2>
                <p className="text-gray-600 mb-6 text-sm sm:text-base">{t.emptyDesc}</p>
                <Link to="/products">
                  <Button className="bg-gradient-to-r from-capsule-teal to-capsule-aqua hover:from-capsule-teal-dark hover:to-capsule-teal text-white rounded-xl">
                    {t.browseProducts}
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </Link>
              </CardContent>
            </Card>
          ) : (
            // Wishlist Grid
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {wishlist.map((item) => {
                const product = item.product;
                if (!product) return null;
                
                return (
                  <Card 
                    key={item.id} 
                    className="group shadow-lg border-0 rounded-2xl overflow-hidden hover:shadow-xl transition-all duration-300"
                    data-testid={`wishlist-item-${product.id}`}
                  >
                    <div className="relative aspect-square bg-gradient-to-br from-gray-50 to-gray-100">
                      <img 
                        src={product.image_url} 
                        alt={getProductName(product)}
                        className="w-full h-full object-contain p-4 group-hover:scale-105 transition-transform duration-300"
                      />
                      {product.is_new && (
                        <span className="absolute top-3 left-3 bg-gradient-to-r from-capsule-teal to-capsule-aqua text-white text-xs font-bold px-3 py-1 rounded-full flex items-center gap-1">
                          <Sparkles className="w-3 h-3" />
                          NEW
                        </span>
                      )}
                      <button
                        onClick={() => removeFromWishlist(product.id)}
                        className="absolute top-3 right-3 w-10 h-10 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center shadow-md hover:bg-red-50 transition-colors group/btn"
                        title={t.remove}
                      >
                        <Heart className="w-5 h-5 text-capsule-salmon fill-capsule-salmon group-hover/btn:scale-110 transition-transform" />
                      </button>
                    </div>
                    
                    <CardContent className="p-4">
                      <h3 className="font-semibold text-gray-900 mb-2 line-clamp-2">
                        {getProductName(product)}
                      </h3>
                      
                      <div className="flex items-center justify-between mb-4">
                        <div>
                          {hasVariants(product) && (
                            <span className="text-xs text-gray-500">{t.from}</span>
                          )}
                          <span className="text-lg font-bold text-capsule-teal ml-1">
                            {formatPrice(getDisplayPrice(product))}
                          </span>
                        </div>
                      </div>
                      
                      <div className="flex gap-2">
                        <Link to={`/product/${product.id}`} className="flex-1">
                          <Button 
                            className="w-full bg-gradient-to-r from-capsule-teal to-capsule-aqua hover:from-capsule-teal-dark hover:to-capsule-teal text-white rounded-xl"
                          >
                            <ShoppingCart className="w-4 h-4 mr-2" />
                            {t.viewProduct}
                          </Button>
                        </Link>
                        <Button
                          variant="outline"
                          size="icon"
                          onClick={() => removeFromWishlist(product.id)}
                          className="border-red-200 text-red-500 hover:bg-red-50 rounded-xl"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default WishlistPage;
